import React from 'react';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast, type Toast as ToastType, type ToastVariant } from '../../contexts/ToastContext';

const variantConfig: Record<ToastVariant, {
    icon: React.FC<{ className?: string }>;
    iconColorDark: string;
    iconColorLight: string;
    bgDark: string;
    bgLight: string;
    borderDark: string;
    borderLight: string;
}> = {
    success: {
        icon: CheckCircle,
        iconColorDark: 'text-green-400',
        iconColorLight: 'text-green-600',
        bgDark: 'bg-green-900/20',
        bgLight: 'bg-green-50',
        borderDark: 'border-green-500/30',
        borderLight: 'border-green-200',
    },
    error: {
        icon: AlertCircle,
        iconColorDark: 'text-red-400',
        iconColorLight: 'text-red-600',
        bgDark: 'bg-red-900/20',
        bgLight: 'bg-red-50',
        borderDark: 'border-red-500/30',
        borderLight: 'border-red-200',
    },
    warning: {
        icon: AlertTriangle,
        iconColorDark: 'text-yellow-400',
        iconColorLight: 'text-yellow-600',
        bgDark: 'bg-yellow-900/20',
        bgLight: 'bg-yellow-50',
        borderDark: 'border-yellow-500/30',
        borderLight: 'border-yellow-200',
    },
    info: {
        icon: Info,
        iconColorDark: 'text-blue-400',
        iconColorLight: 'text-blue-600',
        bgDark: 'bg-blue-900/20',
        bgLight: 'bg-blue-50',
        borderDark: 'border-blue-500/30',
        borderLight: 'border-blue-200',
    },
};

interface ToastItemProps {
    toast: ToastType;
    onDismiss: (id: string) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onDismiss }) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const config = variantConfig[toast.variant];
    const Icon = config.icon;

    return (
        <div
            role="alert"
            aria-live="polite"
            className={`
                flex items-start gap-3 p-4 rounded-lg border shadow-lg
                animate-in slide-in-from-right-full duration-300
                ${isDark ? config.bgDark : config.bgLight}
                ${isDark ? config.borderDark : config.borderLight}
            `}
        >
            <Icon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${isDark ? config.iconColorDark : config.iconColorLight}`} />
            <div className="flex-1 min-w-0">
                <p className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {toast.message}
                </p>
                {toast.action && (
                    <button
                        onClick={toast.action.onClick}
                        className={`
                            mt-2 text-sm font-medium transition-colors
                            ${isDark
                                ? 'text-storgia-light-blurple hover:text-storgia-blurple'
                                : 'text-indigo-600 hover:text-indigo-700'
                            }
                        `}
                    >
                        {toast.action.label}
                    </button>
                )}
            </div>
            <button
                onClick={() => onDismiss(toast.id)}
                aria-label="Dismiss notification"
                className={`
                    flex-shrink-0 p-1 rounded transition-colors
                    ${isDark
                        ? 'text-gray-400 hover:text-white hover:bg-white/10'
                        : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                    }
                `}
            >
                <X className="h-4 w-4" />
            </button>
        </div>
    );
};

export const ToastContainer: React.FC = () => {
    const { toasts, removeToast } = useToast();

    if (toasts.length === 0) return null;

    return (
        <div
            aria-label="Notifications"
            className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm w-full pointer-events-none"
        >
            {toasts.map(toast => (
                <div key={toast.id} className="pointer-events-auto">
                    <ToastItem toast={toast} onDismiss={removeToast} />
                </div>
            ))}
        </div>
    );
};

export { ToastItem };
